import { useState, useEffect } from "react"
import {
  loadStripe,
  Stripe,
  StripePaymentElementOptions,
  StripeElementsOptionsClientSecret,
  StripeElementsOptions,
} from "@stripe/stripe-js"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { useStripeAppearance } from "source/shared/hooks/useStripeAppearance"

const paymentElementOptions = {
  layout: {
    type: "tabs",
    defaultCollapsed: false,
  },
  paymentMethodOrder: ["card"],
  wallets: {
    applePay: "never",
    googlePay: "never",
  },
}

export function useStripeConfig({
  publishable_key,
  account_identifier,
}: {
  publishable_key: string
  account_identifier: string
}) {
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [clientSecret, setClientSecret] = useState<
    StripeElementsOptionsClientSecret | undefined
  >(undefined)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  const appearance = useStripeAppearance()

  useEffect(() => {
    const fetchStripe = async () => {
      try {
        const stripeResponse = await loadStripe(publishable_key, {
          stripeAccount: account_identifier,
        })
        setStripe(stripeResponse)

        const attributes: { return_url?: string } = {}
        if (window.ReactNativeWebView) {
          attributes.return_url = "church-center://payments/oauth-noredirect"
        }

        const { data } = await sessionApiClient.post(
          "/giving/v2/me/stripe_setup_intent",
          {
            data: {
              attributes,
            },
          },
        )
        setClientSecret(data?.attributes?.client_secret)

        setError(null)
      } catch (error: unknown) {
        setError(error as string)
        setStripe(null)
        setClientSecret(undefined)
      } finally {
        setLoading(false)
      }
    }

    fetchStripe()
  }, [publishable_key, account_identifier])

  return {
    stripe: stripe as Stripe,
    stripeOptions: {
      clientSecret,
      appearance,
    } as StripeElementsOptions,
    paymentElementOptions: paymentElementOptions as StripePaymentElementOptions,
    loading,
    error,
  }
}
